import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface Recipe {
  id: number;
  title: string;
  category: string;
  image: string;
  difficulty: string;
  time: string;
  servings: string;
  description: string;
  ingredients: string[];
  instructions: string[];
  featured: boolean;
  tips?: string;
  nutritionalInfo?: string;
}

export default function Receitas() {
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [favorites, setFavorites] = useState<number[]>([]);

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle category change with scroll to top
  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    scrollToTop();
  };

  const categories = [
    { id: 'todos', name: 'Todas as Receitas' },
    { id: 'culinarios', name: 'Pratos Principais' },
    { id: 'sobremesas', name: 'Sobremesas' },
    { id: 'bebidas', name: 'Bebidas' },
    { id: 'aperitivos', name: 'Aperitivos' }
  ];

  const heroImages = [
    'https://static.readdy.ai/image/3b71a02bb86d4e011a6e698798aa6f47/8ef427e1fdc039f627fa3f6f71c893cd.png',
    'https://static.readdy.ai/image/3b71a02bb86d4e011a6e698798aa6f47/95a02f48619e621fb8723e2d86e39f1f.png',
    'https://readdy.ai/api/search-image?query=premium%20hot%20chocolate%20drink%20in%20elegant%20mug%2C%20rich%20creamy%20texture%2C%20whipped%20cream%20topping%2C%20cocoa%20powder%20dusting%2C%20professional%20beverage%20presentation%2C%20warm%20cozy%20atmosphere&width=1920&height=600&seq=heroRecipe3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=crispy%20vegetable%20tempura%20on%20white%20plate%2C%20golden%20brown%20coating%2C%20assorted%20vegetables%2C%20Japanese%20style%20preparation%2C%20professional%20food%20presentation%2C%20light%20and%20airy%20batter&width=1920&height=600&seq=heroRecipe4&orientation=landscape',
    'https://static.readdy.ai/image/3b71a02bb86d4e011a6e698798aa6f47/ee654af1ed4526de2502554f7d020ccf.png',
    'https://static.readdy.ai/image/3b71a02bb86d4e011a6e698798aa6f47/32c9bc51ad1aa409a07d4228abfdb6ff.png'
  ];

  // Auto-rotate images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const recipes: Recipe[] = [
    {
      id: 1,
      title: 'Bife Acebolado com Amaciante de Carnes Tecnutri',
      category: 'culinarios',
      image: 'https://readdy.ai/api/search-image?query=professional%20beef%20steak%20with%20caramelized%20onions%20on%20white%20plate%2C%20commercial%20kitchen%20preparation%2C%20tender%20juicy%20meat%2C%20restaurant%20quality%20dish%2C%20garnished%20with%20herbs%2C%20elegant%20food%20presentation&width=600&height=400&seq=recipe1&orientation=landscape',
      difficulty: 'Fácil',
      time: '30 min',
      servings: '4 porções',
      description: 'Bife macio e suculento preparado com nosso amaciante premium, acompanhado de cebolas douradas.',
      ingredients: ['500g de carne bovina', '2 colheres de Amaciante Premium TecNutri', '2 cebolas grandes', 'Temperos a gosto', 'Óleo para fritura', 'Sal e pimenta'],
      instructions: [
        'Tempere a carne com sal, pimenta e o Amaciante Premium TecNutri',
        'Deixe marinar por 30 minutos em temperatura ambiente',
        'Corte as cebolas em fatias e refogue até dourar',
        'Grelhe os bifes em fogo alto por 3-4 minutos de cada lado',
        'Sirva com as cebolas por cima e acompanhamentos de sua preferência'
      ],
      tips: 'Para um sabor ainda mais especial, adicione um fio de vinho tinto durante o cozimento das cebolas.',
      nutritionalInfo: 'Cada porção contém aproximadamente 320 kcal, 28g de proteína, 8g de carboidratos.',
      featured: true
    },
    {
      id: 2,
      title: 'Pudim com Leite Tecnutri',
      category: 'sobremesas',
      image: 'https://readdy.ai/api/search-image?query=traditional%20milk%20pudding%20dessert%20with%20caramel%20sauce%2C%20professional%20pastry%20presentation%2C%20creamy%20texture%2C%20golden%20caramel%20topping%2C%20elegant%20dessert%20plating%2C%20commercial%20kitchen%20quality&width=600&height=400&seq=recipe2&orientation=landscape',
      difficulty: 'Médio',
      time: '45 min',
      servings: '8 porções',
      description: 'Pudim cremoso e tradicional feito com nossa mistura especial, perfeito para finalizar qualquer refeição.',
      ingredients: ['1 pacote de Pudim TecNutri', '500ml de leite', '200g de açúcar para calda', 'Essência de baunilha', '3 ovos'],
      instructions: [
        'Prepare a calda derretendo o açúcar até dourar',
        'Despeje a calda na forma e reserve',
        'Bata o leite com o pudim TecNutri até dissolver completamente',
        'Adicione os ovos e a essência de baunilha',
        'Despeje na forma com calda e leve ao forno em banho-maria por 40 minutos'
      ],
      tips: 'Para verificar se está pronto, espete um palito no centro. Se sair limpo, está no ponto.',
      nutritionalInfo: 'Cada porção contém aproximadamente 180 kcal, 6g de proteína, 28g de carboidratos.',
      featured: true
    },
    {
      id: 3,
      title: 'Chocolate Quente com Chantilly',
      category: 'bebidas',
      image: 'https://readdy.ai/api/search-image?query=premium%20hot%20chocolate%20drink%20in%20elegant%20mug%2C%20rich%20creamy%20texture%2C%20whipped%20cream%20topping%2C%20cocoa%20powder%20dusting%2C%20professional%20beverage%20presentation%2C%20warm%20cozy%20atmosphere&width=600&height=400&seq=recipe3&orientation=landscape',
      difficulty: 'Fácil',
      time: '10 min',
      servings: '2 porções',
      description: 'Chocolate quente cremoso e encorpado, preparado com nosso chocolate 50% cacau.',
      ingredients: ['4 colheres de Chocolate em Pó TecNutri', '500ml de leite', 'Açúcar a gosto', 'Chantilly para decorar', 'Canela em pó'],
      instructions: [
        'Aqueça o leite em fogo médio sem deixar ferver',
        'Adicione o chocolate em pó TecNutri mexendo constantemente',
        'Adoce a gosto e continue mexendo até ficar homogêneo',
        'Sirva em canecas e decore com chantilly',
        'Polvilhe canela em pó por cima'
      ],
      tips: 'Para uma versão ainda mais especial, adicione uma pitada de essência de baunilha.',
      nutritionalInfo: 'Cada porção contém aproximadamente 220 kcal, 8g de proteína, 32g de carboidratos.',
      featured: false
    },
    {
      id: 4,
      title: 'Tempura de Legumes Crocante',
      category: 'aperitivos',
      image: 'https://readdy.ai/api/search-image?query=crispy%20vegetable%20tempura%20on%20white%20plate%2C%20golden%20brown%20coating%2C%20assorted%20vegetables%2C%20Japanese%20style%20preparation%2C%20professional%20food%20presentation%2C%20light%20and%20airy%20batter&width=600&height=400&seq=recipe4&orientation=landscape',
      difficulty: 'Médio',
      time: '25 min',
      servings: '6 porções',
      description: 'Legumes frescos envolvidos em massa de tempura leve e crocante, perfeitos como entrada.',
      ingredients: ['1 pacote de Mistura para Tempura TecNutri', 'Legumes variados (abobrinha, berinjela, pimentão)', 'Água gelada', 'Óleo para fritura', 'Molho shoyu para acompanhar'],
      instructions: [
        'Corte os legumes em fatias médias',
        'Prepare a massa misturando a Tempura TecNutri com água gelada',
        'Aqueça o óleo a 180°C',
        'Passe os legumes na massa e frite até dourar',
        'Escorra em papel absorvente e sirva imediatamente'
      ],
      tips: 'A água deve estar bem gelada para garantir a crocância da tempura.',
      nutritionalInfo: 'Cada porção contém aproximadamente 150 kcal, 4g de proteína, 18g de carboidratos.',
      featured: false
    },
    {
      id: 5,
      title: 'Mousse de Chocolate Tecnutri',
      category: 'sobremesas',
      image: 'https://readdy.ai/api/search-image?query=elegant%20chocolate%20mousse%20dessert%20in%20glass%20cups%2C%20rich%20creamy%20texture%2C%20chocolate%20shavings%20garnish%2C%20professional%20pastry%20presentation%2C%20restaurant%20quality%20dessert&width=600&height=400&seq=recipe5&orientation=landscape',
      difficulty: 'Fácil',
      time: '20 min',
      servings: '6 porções',
      description: 'Mousse aerado e cremoso, uma sobremesa irresistível para impressionar seus clientes.',
      ingredients: ['1 pacote de Mousse TecNutri', '300ml de leite gelado', 'Raspas de chocolate', 'Frutas para decorar', 'Folhas de hortelã'],
      instructions: [
        'Bata o leite gelado com o Mousse TecNutri por 3 minutos',
        'Continue batendo até formar picos firmes',
        'Distribua em taças individuais',
        'Leve à geladeira por 2 horas',
        'Decore com raspas de chocolate e frutas antes de servir'
      ],
      tips: 'Para melhor resultado, use leite bem gelado e bata em velocidade alta.',
      nutritionalInfo: 'Cada porção contém aproximadamente 140 kcal, 5g de proteína, 22g de carboidratos.',
      featured: true
    },
    {
      id: 6,
      title: 'Gelatina Refrescante de Maçã Verde',
      category: 'sobremesas',
      image: 'https://readdy.ai/api/search-image?query=refreshing%20green%20apple%20gelatin%20dessert%20in%20elegant%20glasses%2C%20crystal%20clear%20texture%2C%20mint%20garnish%2C%20professional%20dessert%20presentation%2C%20light%20and%20healthy%20dessert&width=600&height=400&seq=recipe6&orientation=landscape',
      difficulty: 'Fácil',
      time: '15 min + 2h geladeira',
      servings: '8 porções',
      description: 'Gelatina zero açúcar com sabor refrescante de maçã verde, ideal para dietas especiais.',
      ingredients: ['1 pacote de Gelatina Zero TecNutri sabor maçã verde', '500ml de água quente', '500ml de água fria', 'Folhas de hortelã', 'Cubos de maçã verde'],
      instructions: [
        'Dissolva a gelatina na água quente mexendo bem',
        'Adicione a água fria e misture',
        'Adicione os cubos de maçã verde',
        'Distribua em formas individuais',
        'Leve à geladeira por 2 horas até firmar'
      ],
      tips: 'Para um visual mais bonito, adicione as folhas de hortelã antes de levar à geladeira.',
      nutritionalInfo: 'Cada porção contém aproximadamente 25 kcal, 2g de proteína, 4g de carboidratos.',
      featured: false
    }
  ];

  const filteredRecipes = recipes.filter(recipe => {
    const matchesCategory = activeCategory === 'todos' || recipe.category === activeCategory;
    const matchesSearch = recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         recipe.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredRecipes = recipes.filter(recipe => recipe.featured);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const openRecipeModal = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
  };

  const closeRecipeModal = () => {
    setSelectedRecipe(null);
  };

  const toggleFavorite = (recipeId: number) => {
    setFavorites(prev => 
      prev.includes(recipeId) 
        ? prev.filter(id => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section with Rotating Images */}
      <section className="relative py-32 overflow-hidden">
        {/* Image Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div
                className="w-full h-full bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url('${image}')`
                }}
              />
            </div>
          ))}
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/15"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Receitas Exclusivas
          </h1>
          <p className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto">
            Descubra receitas especiais desenvolvidas para nossos produtos 
Tecnutri Food Service
          </p>
        </div>

        {/* Image Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex space-x-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-white scale-125' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      <main className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Featured Recipes */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <span className="text-red-600 font-semibold text-lg">Destaques</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">
                Receitas em Destaque
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {featuredRecipes.map((recipe) => (
                <Card key={recipe.id} hover className="group cursor-pointer">
                  <div className="aspect-[4/3] mb-4 overflow-hidden rounded-lg bg-gray-100 relative">
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onClick={() => {
                        openRecipeModal(recipe);
                        scrollToTop();
                      }}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(recipe.id);
                      }}
                      className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        favorites.includes(recipe.id)
                          ? 'bg-red-600 text-white'
                          : 'bg-white/80 text-gray-600 hover:bg-red-600 hover:text-white'
                      }`}
                    >
                      <i className={`${favorites.includes(recipe.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <i className="ri-time-line mr-1"></i>
                        {recipe.time}
                      </span>
                      <span className="flex items-center">
                        <i className="ri-user-line mr-1"></i>
                        {recipe.servings}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        recipe.difficulty === 'Fácil' ? 'bg-green-100 text-green-800' :
                        recipe.difficulty === 'Médio' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {recipe.difficulty}
                      </span>
                    </div>
                    
                    <h3 className="font-semibold text-xl text-gray-900 group-hover:text-red-600 transition-colors">
                      {recipe.title}
                    </h3>
                    
                    <p className="text-gray-600 line-clamp-2">
                      {recipe.description}
                    </p>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        openRecipeModal(recipe);
                        scrollToTop();
                      }}
                    >
                      Ver Receita Completa
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Search and Filter Section */}
          <div className="mb-12">
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Pesquisar receitas..."
                    className="w-full px-6 py-4 rounded-xl border border-gray-300 focus:outline-none focus:ring-4 focus:ring-red-300 text-lg"
                  />
                  <i className="ri-search-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
                <Button type="submit" size="lg">
                  Buscar
                </Button>
              </div>
            </form>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-red-50 hover:text-red-600 shadow-md border border-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* All Recipes Grid */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Todas as Receitas</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredRecipes.map((recipe) => (
                <Card key={recipe.id} hover className="group cursor-pointer">
                  <div className="aspect-[4/3] mb-4 overflow-hidden rounded-lg bg-gray-100 relative">
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onClick={() => {
                        openRecipeModal(recipe);
                        scrollToTop();
                      }}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(recipe.id);
                      }}
                      className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        favorites.includes(recipe.id)
                          ? 'bg-red-600 text-white'
                          : 'bg-white/80 text-gray-600 hover:bg-red-600 hover:text-white'
                      }`}
                    >
                      <i className={`${favorites.includes(recipe.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <i className="ri-time-line mr-1"></i>
                        {recipe.time}
                      </span>
                      <span className="flex items-center">
                        <i className="ri-user-line mr-1"></i>
                        {recipe.servings}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        recipe.difficulty === 'Fácil' ? 'bg-green-100 text-green-800' :
                        recipe.difficulty === 'Médio' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {recipe.difficulty}
                      </span>
                    </div>
                    
                    <h3 className="font-semibold text-lg text-gray-900 group-hover:text-red-600 transition-colors">
                      {recipe.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {recipe.description}
                    </p>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        openRecipeModal(recipe);
                        scrollToTop();
                      }}
                    >
                      Ver Receita Completa
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {filteredRecipes.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-restaurant-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-semibold text-gray-600 mb-2">Nenhuma receita encontrada</h3>
              <p className="text-gray-500">Tente ajustar os filtros ou termo de busca</p>
            </div>
          )}

          {/* Newsletter Section */}
          <div className="mt-16 bg-red-600 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">
              Receba Novas Receitas
            </h3>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Cadastre-se e receba receitas exclusivas desenvolvidas pelos nossos chefs
            </p>
            <form 
              className="max-w-md mx-auto flex gap-4"
              data-readdy-form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const data = new URLSearchParams();
                data.append('email', formData.get('email') as string);
                
                try {
                  const response = await fetch('https://readdy.ai/api/form/d3m055a2otq89lvpsf4g', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: data
                  });
                  
                  if (response.ok) {
                    alert('Cadastro realizado com sucesso! Você receberá nossas receitas exclusivas.');
                    (e.target as HTMLFormElement).reset();
                  } else {
                    alert('Erro ao cadastrar. Tente novamente.');
                  }
                } catch (error) {
                  alert('Erro ao cadastrar. Verifique sua conexão e tente novamente.');
                }
              }}
            >
              <input
                type="email"
                name="email"
                placeholder="Seu e-mail"
                required
                className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-4 focus:ring-red-300"
              />
              <Button type="submit" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                Cadastrar
              </Button>
            </form>
          </div>

        </div>
      </main>

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-900">{selectedRecipe.title}</h2>
              <button
                onClick={closeRecipeModal}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recipe Image */}
                <div className="aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden">
                  <img
                    src={selectedRecipe.image}
                    alt={selectedRecipe.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Recipe Info */}
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedRecipe.difficulty === 'Fácil' ? 'bg-green-100 text-green-800' :
                        selectedRecipe.difficulty === 'Médio' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedRecipe.difficulty}
                      </span>
                      <span className="flex items-center text-gray-600">
                        <i className="ri-time-line mr-1"></i>
                        {selectedRecipe.time}
                      </span>
                      <span className="flex items-center text-gray-600">
                        <i className="ri-user-line mr-1"></i>
                        {selectedRecipe.servings}
                      </span>
                    </div>
                    <p className="text-lg text-gray-600 mb-4">{selectedRecipe.description}</p>
                  </div>

                  {/* Ingredients */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <i className="ri-list-check mr-2 text-green-600"></i>
                      Ingredientes
                    </h4>
                    <ul className="space-y-2">
                      {selectedRecipe.ingredients.map((ingredient, index) => (
                        <li key={index} className="flex items-center text-gray-700">
                          <i className="ri-checkbox-circle-line mr-2 text-green-600"></i>
                          {ingredient}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Instructions */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <i className="ri-book-open-line mr-2 text-blue-600"></i>
                      Modo de Preparo
                    </h4>
                    <ol className="space-y-3">
                      {selectedRecipe.instructions.map((instruction, index) => (
                        <li key={index} className="flex text-gray-700">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                            {index + 1}
                          </span>
                          {instruction}
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Tips */}
                  {selectedRecipe.tips && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                        <i className="ri-lightbulb-line mr-2 text-yellow-600"></i>
                        Dica do Chef
                      </h4>
                      <p className="text-gray-700">{selectedRecipe.tips}</p>
                    </div>
                  )}

                  {/* Nutritional Info */}
                  {selectedRecipe.nutritionalInfo && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                        <i className="ri-heart-pulse-line mr-2 text-green-600"></i>
                        Informações Nutricionais
                      </h4>
                      <p className="text-gray-700">{selectedRecipe.nutritionalInfo}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4">
                    <Button 
                      size="lg" 
                      className="flex-1"
                      onClick={() => toggleFavorite(selectedRecipe.id)}
                    >
                      <i className={`${favorites.includes(selectedRecipe.id) ? 'ri-heart-fill' : 'ri-heart-line'} mr-2`}></i>
                      {favorites.includes(selectedRecipe.id) ? 'Favoritado' : 'Favoritar'}
                    </Button>
                    <Button variant="outline" size="lg" className="flex-1">
                      <i className="ri-share-line mr-2"></i>
                      Compartilhar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
